import { Section } from "@aidkitorg/types/lib/legacy/airtable";
import { NavigationNode, filterNavigation, BlockEdits, ReviewConfiguration } from "@aidkitorg/types/lib/translation/permissions";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { get_deployment, get_rs_host, useAPIPost, useErrorHandler, usePost } from "./API";
import { ApplicantComms, useAuthorizedCommsChannels } from "./Applicant/Comms";
import Notes from "./Applicant/Notes";
import { ModularQuestionPage } from "./ModularQuestionPage";
import { StandardMenu, SubTab, ThreeColumnPage } from "./Components/ThreeColumnPage";
import { safeParse, snakeToEnglish, useInterval } from "./Util";
import History from "./Applicant/History";
import { UnsubmittedHistory } from "./Applicant/UnsubmittedHistory";
import { AdminTab } from "./Applicant/AdminTab";
import { ConfigurationContext, PublicConfigurationContext } from "./Context";
import { useLocalizedStrings } from "./Localization";
import { ApplicantIdentities } from "./ApplicantIdentities";
import { FileHub } from "./FileHub";
import { captureException } from "@sentry/react";
import { FraudFlags } from "./Applicant/FraudFlags";
import { LoggedInConfig } from "@aidkitorg/types/lib/config";
import { ApplicantMeetings } from "./Applicant/Meetings";
import { Text, interfaceNumber } from "@aidkitorg/types/lib/survey";
import { ThirdPartyCheckStatusTab } from "./Applicant/ThirdPartyCheckStatus";
import { ActivityLog } from "./Applicant/ActivityLog";
import { useAsyncEffect } from "./Hooks/AsyncEffect";
import { Icon, Loading } from "@aidkitorg/component-library";
import { ApplicantProvider } from "./Contexts/ApplicantContext";
import { ApplicantPaymentPage } from "./Applicant/PaymentPage";
import { CompileExpressionToJS } from "@aidkitorg/types/lib/translation/expr_to_js";
import { QuickJS } from "@aidkitorg/types/lib/quickJS";
import { useRelatedCases } from "./SupportUtil";
import { evalDistroConditional } from "@aidkitorg/types/lib/eval";
import { ReviewDetails, ReviewWrapper, showV2Review } from "./ReviewWrapper";

const isApplicantNotFoundError = (error: unknown) => {
  return typeof error === 'object' && error && 'error' in error && error.error === 'No Applicant Found';
};

export function ApplicantPage(props?: { 
  readonly?: boolean;
  uid?: string;
  subsurvey?: string;
  applicantDash?: string;
}) {
  const params = useParams() as Record<string, string>;
  const uid = props?.uid || params.uid;
  const dynamoAppId = params.dynamoAppId;
  
  // Use state to track readonly status instead of mutating props
  const [isReadonly, setIsReadonly] = useState(props?.readonly || false);
  const [applicantNotFound, setApplicantNotFound] = useState(false);

  // Sync state with props changes
  useEffect(() => {
    if (props?.readonly !== undefined) {
      setIsReadonly(props.readonly);
    }
  }, [props?.readonly]);

  const applicantDash = props?.applicantDash || params.applicantDash;
  const { hash, pathname } = useLocation();
  const [nav, setNav] = useState<NavigationNode[]>([]);
  const [info, setInfo] = useState<{ [key: string]: any }>({});
  const [fetchedInfo, setFetchedInfo] = useState<{ [key: string]: any }>({});
  const [applicantTitle, setApplicantTitle] = useState('');
  const getSurvey = usePost("/survey");
  const getNav = usePost("/navigation");
  const config = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const L = useLocalizedStrings();
  const commsChannels = useAuthorizedCommsChannels({ config });
  const getLoggedInConfig = usePost('/program/logged_in_configuration');
  const [loggedInConfig, setLoggedInConfig] = useState<LoggedInConfig | undefined>();
  const [disallowed, setDisallowed] = useState<string[] | null>(null);
  const [applicant, setApplicant] = useState<Awaited<ReturnType<typeof getEverything>> | null>(null);
  const errorHandler = useErrorHandler();
  const getEverything = usePost('/applicant/get_everything', {
    handleErrors: (error, handleToast) => {
      if (isApplicantNotFoundError(error)) {
        setApplicantNotFound(true);
      } else {
        errorHandler(error, handleToast);
      }
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const shouldFastForward = useRef<boolean | undefined>();
  const blockFurtherEdits = useRef<BlockEdits | null>(null);
  const [review, setReview] = useState<ReviewDetails | undefined>(undefined);
  const [subsurvey, setSubsurvey] = useState(props?.subsurvey || params.subsurvey);
  
  const [surveySections, setSurveySections] = useState<{ sections: Section[] }>({ sections: [] });
  const [activeSections, setActiveSections] = useState<{ sections: Section[] }>({ sections: [] });

  useAsyncEffect(async () => {
    const applicantId = uid || dynamoAppId;

    const [response, sections] = await Promise.all([
      getNav({ applicant: applicantId }),
      getSurvey({}) as any
    ]);
    
    setNav(response.navigation);
    setSurveySections(sections);
  }, [uid, dynamoAppId]);

  // We prefer to have a subsurvey so that we can follow the complete picture of the rules applied to that subsurvey.
  // Like who should view it, how it should be blocked, etc.
  useAsyncEffect(async () => {
    let subsurveyToRender = props?.subsurvey;
    const applicant = uid || dynamoAppId;

    const findFirstSubsurvey = (nav: NavigationNode[]): string | undefined => {
      for (const node of nav) {
        if (typeof node.name === 'string' && node.name.startsWith('/p/')) {
          return node.name.slice(3);
        }
        if (node.subnodes) {
          const found = findFirstSubsurvey(node.subnodes);
          if (found) {
            return found;
          }
        }
      }
    }

    const url = window.location.href;
    if (!subsurveyToRender) {
      // first, make sure we don't have something else set in the 
      // URL that we want to respect.
      let section = surveySections.sections[surveySections.sections?.findIndex((section: Section) => section["English Content"]?.trim() === decodeURIComponent(hash.slice(1)))];
      if (section) {
        console.log("Section found", section, "returning without a subsurvey");
        setSubsurvey('');
        return;
      }
      
      // subsurvey may have already been set by this useEffect, and might already be in the URL. If it is, we don't want to overwrite it.
      if (subsurvey && window.location.pathname.includes(subsurvey)) return;

      // Don't update URL if this is an applicant dashboard or file hub
      if (applicantDash) return;

      subsurveyToRender = findFirstSubsurvey(nav) || '';
      if (subsurveyToRender) {
        const newUrl = url.replace(applicant, applicant + '/' + subsurveyToRender);
        window.history.replaceState({}, '', newUrl);
      }
    }

    setSubsurvey(subsurveyToRender || '');
  }, [props?.subsurvey, params.subsurvey, subsurvey, uid, dynamoAppId, nav, window.location.href, surveySections, applicantDash]);


  const [fraudFlagsLength, setFraudFlagsLength] = useState<number | null>(null);

  useEffect(() => {
    if (!loggedInConfig) {
      (async () => {
        setLoggedInConfig(await getLoggedInConfig({}));
      })();
    }
  }, [loggedInConfig]);

  const refreshInProgress = useRef<Promise<any> | null>(null)
  const currentApplicantId = useRef(uid || dynamoAppId);

  async function refreshApplicant(extras?: Parameters<typeof getEverything>[0]["extras"]) {
    if (refreshInProgress.current) return;

    let app;
    try {
      const applicantId = currentApplicantId.current;
      refreshInProgress.current = getEverything({ uid: applicantId, extras });
      app = await refreshInProgress.current;

      if (currentApplicantId.current !== applicantId) {
        // This info is for the wrong applicant
        return;
      }

      if (app && app.ingestedUid) {
        const appPath = interfaceNumber(publicConfig?.interface?.version) >= 1 ? '/a/' : '/applicant/';
        window.location.href = appPath + app.ingestedUid;
      }
      if (app?.defaultApplicantPage && !hash.length && pathname.match(new RegExp(`^\/a\/${uid}\/?$`))) {
        window.location.href = `/a/${uid}/` + app.defaultApplicantPage;
      }
    } finally {
      refreshInProgress.current = null;
    }

    // We may have changed things since in the await
    if (Object.keys(pendingChanges.current).length > 0) {
      return;
    }

    if (app) {
      setInfo(app.info || {});
      setFetchedInfo(app.info || {});
      setApplicant(app);
    }
  }
  useEffect(() => {
    refreshApplicant({ defaultApplicantPage: true });
  }, [])

  useEffect(() => {
    currentApplicantId.current = uid || dynamoAppId;
  }, [uid, dynamoAppId]);

  useAsyncEffect(async () => {
    if (surveySections.sections.length === 0) {
      return;
    }

    // Required for determining if we are showing the new V2 review mode.
    if (!config.user?.email) {
      return;
    }

    setIsLoading(true);
    setReview(undefined);
    shouldFastForward.current = undefined;

    // at this point, if we don't have a subsurvey, we are never going to get one. 
    // so just find the matching section (or the first one) and render it.
    if (!subsurvey || nav.length === 0) {
      let section = surveySections.sections[surveySections.sections?.findIndex((section: Section) => section["English Content"]?.trim() === decodeURIComponent(hash.slice(1)))];
      const activeSections = [];
      if (!section && surveySections.sections.length > 0) {
        console.log("No section found, setting to first section", surveySections.sections[0]);
        section = surveySections.sections[0];
      }
      if (section) {
        activeSections.push(section);
        setActiveSections({ sections: activeSections });
      };
      setIsLoading(false);
      return;
    }

    // Traverse the navigation until we find the appropriate subsurvey
    const findSections = (nav: NavigationNode[]): NavigationNode[] | null => {
      for (const node of nav) {
        if (node.name === '/p/' + subsurvey && node.subnodes) {
          shouldFastForward.current = node.shouldFastForward;
          blockFurtherEdits.current = node.blockFurtherEdits || null;
          if (showV2Review(node.reviewConfiguration, config.user?.email || '')) {
            setReview({
              ...node.reviewConfiguration,
              title: node.subsurveyTitle || snakeToEnglish(subsurvey),
            } as ReviewDetails);
          }

          return node.subnodes;
        }
        if (node.subnodes) {
          const found = findSections(node.subnodes);
          if (found) {
            return found;
          }
        }
      }
      return null;
    }

    const subsurveysections = findSections(nav);
    const activeSections = [];
    for (const section of subsurveysections || []) {
      const c = surveySections.sections.filter((s: Section) => s["English Content"] === section.name || s["English Content"] === (section.name as Text).en);

      if (c.length === 1) {
        activeSections.push(c[0])
      } else if (c.length > 0) {
        // Perform string matching on path injected into sectionIdentifier in order to differentiate between sections with the same title
        const sectionPath = section.sectionPath
        let matchedContent: any[] = []

        if (sectionPath && sectionPath !== "") {
          // The Subsurvey kind in matched content should have the path for the subsurvey
          matchedContent = c.filter((s: Section) => s["Questions"].find((q) => (q["Kind"] === 'Subsurvey' && q["Question"] === sectionPath)))
        }

        // If none of this works, default to pushing the first content found
        if (matchedContent.length > 0) {
          activeSections.push(matchedContent[0])
        } else {
          activeSections.push(c[0])
        }
      }
    }

    if (!activeSections.length && subsurveysections?.[0]) {
      // If we don't have any active sections, set the first section as active
      console.log("No active sections, setting to first section", subsurveysections?.[0]);
      activeSections.push(subsurveysections?.[0]);
    }
    console.log("Active sections", activeSections);
    setActiveSections({ sections: activeSections });
    setIsLoading(false);
  }, [subsurvey, uid, dynamoAppId, hash, nav, surveySections, config.user?.email]);

  useAsyncEffect(async () => {
    if (blockFurtherEdits.current?.block_when && blockFurtherEdits.current.block_users_tagged?.some((role) => config.roles?.includes(role))) {
      const block = await evalDistroConditional(blockFurtherEdits.current.block_when, info || {});
      if (block) setIsReadonly(true);
    }
  }, [info, blockFurtherEdits.current, config.roles]);

  useEffect(() => {
    // temporary convention till we can implement explicit RBAC.
    // this scoops up all "no-[name]" tags and lets us use em 
    // in the tab array below to more easily control what is shown.
    //
    // TODO: Remove this once we have a better RBAC!
    if (config.roles) {
      setDisallowed(Array.from((config.roles || []) as string[]).filter((r) => r.startsWith('no-')).map(r => r.replace('no-', '')));
    }
  }, [config.roles]);

  useAsyncEffect(async () => {
    const defaultValue = info["legal_name"] || "Applicant";
    if (!publicConfig?.interface?.applicantDisplayName) {
      setApplicantTitle(defaultValue);
      return;
    }

    try {
      const js = CompileExpressionToJS(publicConfig.interface.applicantDisplayName.valueExpr);
      setApplicantTitle(await QuickJS.compute(js, { info }));
    } catch (err) {
      captureException('Error computing applicant display name', { extra: { err, uid, deployment: get_deployment() } })
      setApplicantTitle(defaultValue);
    }
  }, [publicConfig, info]);

  // We store a ref of pending changes to prevent race conditions between other callbacks
  // that might otherwise close around this state
  const pendingChanges = useRef<Record<string, any>>({})
  const pendingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveInfoRS = useAPIPost(get_rs_host() + "/compute_and_save", { includeTokenInData: true });

  function saveInfo(update: Record<string, any>) {
    if (isReadonly) return;

    // Figure out what changed (note, we know that "deleted" keys are set as '' so we don't
    // need to search for keys that have disappeared)
    setInfo(update);
    const changes = Object.keys(update).reduce((prev, k: string) => {
      if (update[k] !== info[k]) {
        prev[k] = update[k];
      }
      return prev;
    }, {} as Record<string, any>)

    if (Object.keys(changes).length) {
      // Update any pending changes
      Object.assign(pendingChanges.current, changes);

      // Debounce saving by 1 second
      if (pendingTimeout.current) {
        clearTimeout(pendingTimeout.current)
      }
      pendingTimeout.current = setTimeout(async () => {
        const toSave = { ...pendingChanges.current };

        const result = await saveInfoRS({
          changedKeys: toSave,
          uid,
          deploymentKey: get_deployment()
        })
        if (!result) {
          captureException("Error saving info", { 
            extra: { 
              uid, 
              program: get_deployment(),
              keys: Object.keys(toSave)
            }})
        }
        // We need to delete the keys that we attempted to save 
        // even if there was an error else this while loop will 
        // never terminate
        if (result?.error) {
          console.error("Error saving", result.error, Object.keys(pendingChanges.current).join(","));
          captureException(result.error, { extra: { uid, deploymentKey: get_deployment() } });
        }
        for (const k of Object.keys(toSave)) {
          if (pendingChanges.current[k] === toSave[k]) {
            delete pendingChanges.current[k];
          }
        }
        // TODO: eventually remove this! We are putting this in to allow BRF RS calls to take longer without erroring.
        // We would like to remove this once we have made smarter changes to RS.
      }, get_deployment() === 'brf' ? 1500 : 1000);
    }
  }

  // Refresh every 3 seconds unless there are pending changes or the applicant does not exist
  useInterval(() => {
    if (applicantNotFound) {
      return;
    }

    if (Object.keys(pendingChanges.current).length > 0) {
      console.log("Pending changes", pendingChanges.current)
      return;
    }
    refreshApplicant();
  }, 3000);

  const relatedSupportCases = useRelatedCases(uid);

  const applicantContext = useMemo(() => ({
    info,
    update: saveInfo,
  }), [info, saveInfo]);

  if (applicantNotFound) {
    return <ThreeColumnPage
      main={<div className="flex flex-col items-center justify-center h-full">
        <Icon name='ExclamationTriangleIcon' type='solid' className='text-yellow-800 h-14 w-14' />
        <h3 className="text-center">{L.applicant_not_found}</h3>
      </div>}
    />;
  }

  // Common props for ModularQuestionPage
  const modularQuestionPageProps = {
    sections: activeSections as any,
    uid: uid,
    viewInfo: { readonly: isReadonly ? 'true' : undefined } as const,
    submit: async () => { },
    blockFurtherEdits: blockFurtherEdits.current || undefined,
    shouldFastForward: shouldFastForward.current === undefined ? undefined : shouldFastForward,
    noNavBar: subsurvey ? undefined : true as true,
    sequential: true,
    Viewer: 'screener' as const,
    review: review,
  };

  // Determine which main content to render
  const renderMainContent = () => {
    if (pathname.startsWith('/payments/')) {
      return <ApplicantPaymentPage applicant={uid} />;
    }

    if (applicantDash) {
      return <ApplicantIdentities applicant={uid} path={applicantDash} />;
    }
    
    if (review) {
      return (
        <ReviewWrapper>
          <ModularQuestionPage {...modularQuestionPageProps} />
        </ReviewWrapper>
      );
    }
    
    return <ModularQuestionPage {...modularQuestionPageProps} />;
  };

  return <ApplicantProvider context={applicantContext}>
    <ThreeColumnPage
      nav={isLoading ? [] : nav}
      review={review}
      title={applicantTitle}
      main={<Loading loading={isLoading}>
        {renderMainContent()}
      </Loading>}
      subtabs={disallowed?.includes('tabs') ? undefined : [
        {
          key: 'notes',
          name: L.applicant.notes.title,
          show: disallowed && !disallowed.includes('notes'),
          children: <>
            <Notes
              uid={dynamoAppId || uid}
              canDeleteNotes={true}
              unsubmittedApp={!!dynamoAppId}
              relatedSupportCases={relatedSupportCases}
              noteTags={loggedInConfig?.capabilities?.noteTags}
            />
          </>,
        },
        ...(commsChannels.map(channel => ({
          key: 'comms' + channel,
          name: 'Comms' + (channel !== 'default' ? ` (${channel})` : ''),
          show: disallowed && !disallowed.includes('comms') && commsChannels && commsChannels.length > 0,
          children: <ApplicantComms 
            key={'comms-' + channel} 
            uid={dynamoAppId || uid} 
            info={fetchedInfo} 
            setUnhandledCount={() => { }} 
            canSendComms={true} 
            channel={channel} 
            relatedSupportCases={relatedSupportCases?.filter(sc => sc.channel === channel)} />
        }))),
        {
          key: 'activity-log',
          name: L.applicant.activity_log.title,
          show: (() => {
            const activityLog = loggedInConfig?.capabilities?.activityLog;
            if (!activityLog) return false;
            const limitedTo = activityLog.limitedToUsersTagged;
            if (!limitedTo?.length) return true;
            return config.roles?.some((role: string) => limitedTo.includes(role)) ?? false;
          })(),
          children: <ActivityLog 
            uid={uid}
            relatedSupportCases={relatedSupportCases}
          />,
        },
        {
          name: 'IA Notes',
          key: 'ia-notes',
          show: disallowed && ((config.roles || '').indexOf('internal audit') !== -1),
          children: <>
            <Notes
              uid={uid}
              canDeleteNotes={true}
              internalAudit={true}
              noteTags={loggedInConfig?.capabilities?.noteTags}
            />
          </>
        },
        {
          key: 'meetings',
          name: L.applicant.meeting.meetings,
          show: disallowed && !disallowed.includes('meetings') && loggedInConfig?.comms?.videoCalling?.enabled,
          children: <ApplicantMeetings applicantId={uid} info={fetchedInfo} />
        },
        {
          key: 'history',
          name: L.applicant.history.title,
          show: disallowed && !disallowed.includes('history'),
          children: !dynamoAppId ? <>
            <History uid={uid} canViewChanges={true} />
          </>
            : <> <UnsubmittedHistory info={info} /> </>
        },
        ...(!dynamoAppId ? [{
          key: 'debug',
          name: 'Debug',
          show: disallowed && !disallowed.includes('debug'),
          children: <>
            <AdminTab uid={uid} />
          </>
        }] : []),
        ...(!dynamoAppId ? [{
          key: 'fraud',
          name: L.applicant.fraud.title + (fraudFlagsLength !== null ? ` (${fraudFlagsLength})` : ''),
          show: disallowed && !disallowed.includes('fraud') && loggedInConfig?.fraud?.enableTab && ((config.roles || '').includes('admin') || (config.roles || '').includes('reviewer')),
          children: <FraudFlags uid={uid} setLength={(l) => setFraudFlagsLength(l)} />
        }] : []),
        {
          key: 'file-hub',
          name: 'File Hub',
          show: (() => {
            const fileHubConfig = loggedInConfig?.capabilities?.fileHub;
            if (!fileHubConfig) return false;
            const limitedTo = fileHubConfig.limitedToUsersTagged;
            if (!limitedTo?.length) return true;
            return config.roles?.some((role: string) => limitedTo.includes(role)) ?? false;
          })(),
          children: <FileHub applicant={uid} />
        },
        ...(!dynamoAppId ? [{
          key: 'tpcs',
          name: 'Third Party Checks',
          show: disallowed && !disallowed.includes('history') && (
            publicConfig?.capabilities?.thirdPartyCheckStatusTab && config.roles?.some((role: string) => {
              return publicConfig?.capabilities?.thirdPartyCheckStatusTab?.includes(role)
            })) || false,
          children: <ThirdPartyCheckStatusTab uid={uid} info={fetchedInfo} saveInfo={saveInfo} isAdmin={config.roles?.includes('admin')} />
        }] : [])] as SubTab[]}
    />
  </ApplicantProvider>
}
